<template>
  <div class="card">
    <div class="card-header">
      <h3>Internal Notes</h3>
      <small class="form-text text-muted">Visible to staff only. Adding a note also writes a NOTE_ADDED audit row.</small>
    </div>
    <div class="card-body">
      <div class="mb-3">
        <textarea class="form-control" rows="2" v-model="body" placeholder="Add an internal note..."></textarea>
        <div class="text-end mt-2">
          <button class="btn btn-primary btn-sm" :disabled="isSaving || !body.trim()" @click="addNote">
            <span v-if="isSaving" class="spinner-border spinner-border-sm me-2"></span>
            {{ isSaving ? 'Saving...' : 'Add Note' }}
          </button>
        </div>
      </div>

      <div v-if="isLoading" class="text-center py-3">
        <span class="spinner-border spinner-border-sm me-2"></span> Loading...
      </div>
      <div v-else-if="notes.length === 0" class="text-muted">No notes yet.</div>
      <ul v-else class="list-group">
        <li class="list-group-item" v-for="note in notes" :key="note.id">
          <div class="d-flex justify-content-between">
            <span class="text-muted small">{{ formatDate(note.createdOn) }}</span>
          </div>
          <div>{{ note.body }}</div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { showToast, notificationType } from '../utility/toastNotificationUtility';

const props = defineProps({
  walletId: { type: String, required: true }
});

const notes = ref([]);
const body = ref('');
const isLoading = ref(false);
const isSaving = ref(false);

const formatDate = (value) => value ? new Date(value).toLocaleString() : '';

const loadNotes = async () => {
  isLoading.value = true;
  try {
    const response = await fetch(`/api/admin/wallets/${props.walletId}/notes`);
    if (response.ok) {
      notes.value = await response.json();
    }
  } catch (error) {
    console.error('Error loading wallet notes:', error);
  } finally {
    isLoading.value = false;
  }
};

const addNote = async () => {
  if (!body.value.trim() || isSaving.value) return;
  isSaving.value = true;
  try {
    const response = await fetch(`/api/admin/wallets/${props.walletId}/notes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ body: body.value.trim() })
    });
    if (!response.ok) {
      const message = await response.text();
      showToast({ title: 'Error', body: message || 'Unable to add note.', type: notificationType.ERROR, delay: 5000 });
      return;
    }
    body.value = '';
    await loadNotes();
    showToast({ title: 'Success', body: 'Note added.', type: notificationType.SUCCESS });
  } catch (error) {
    console.error('Error adding wallet note:', error);
    showToast({ title: 'Oops..', body: 'Encountered an internal error. Please contact support.', type: notificationType.ERROR, delay: 7000 });
  } finally {
    isSaving.value = false;
  }
};

onMounted(loadNotes);
</script>
